/**
 * Shared OTP machine codes + helpers for signup confirm and both forgot-password doors.
 * Keep in sync with Lambda/Backend auth_service.utils.otp_errors / app.errors.otp.
 */

export const OTP_ERROR_CODES = {
  OTP_INVALID: "OTP_INVALID",
  OTP_EXPIRED: "OTP_EXPIRED",
  OTP_LIMIT: "OTP_LIMIT",
  COGNITO_UNAVAILABLE: "COGNITO_UNAVAILABLE",
  OTP_FAILED: "OTP_FAILED",
  EMAIL_ALREADY_CONFIRMED: "EMAIL_ALREADY_CONFIRMED",
};

/** Axios transport codes that must not override API errors[0].code. */
const AXIOS_TRANSPORT_CODES = new Set([
  "ERR_NETWORK",
  "ECONNABORTED",
  "ERR_CANCELED",
  "ETIMEDOUT",
  "ECONNREFUSED",
  "ERR_BAD_RESPONSE",
]);

/**
 * Prefer API body errors[0].code over axios transport `error.code` when a response exists.
 * Used by getApiErrorCode; also exported for callers that already hold a raw axios error.
 */
export function preferApiErrorCode(error) {
  if (!error) return "";

  const bodyErrors = error.response?.data?.errors;
  if (Array.isArray(bodyErrors) && bodyErrors.length > 0) {
    const code = bodyErrors[0]?.code;
    if (typeof code === "string" && code.trim()) return code.trim();
  }

  // AuthServiceError (and similar) already carry the API code.
  if (
    typeof error.code === "string" &&
    error.code.trim() &&
    !AXIOS_TRANSPORT_CODES.has(error.code)
  ) {
    return error.code.trim();
  }

  return "";
}

/** True when the code means the OTP is no longer usable — enable Resend. */
export function isOtpExpiredCode(code) {
  return code === OTP_ERROR_CODES.OTP_EXPIRED;
}

/** True when Cognito (or the auth API) is temporarily unreachable. */
export function isCognitoUnavailable(code) {
  return code === OTP_ERROR_CODES.COGNITO_UNAVAILABLE;
}

/** True when rate-limited — keep Resend gated with wait copy. */
export function isOtpLimitCode(code) {
  return code === OTP_ERROR_CODES.OTP_LIMIT;
}

/** Axios's default Error.message for HTTP failures — not user-facing. */
function isAxiosStatusMessage(message) {
  return /^Request failed with status code \d+$/i.test(message);
}

/**
 * Resolve user-facing OTP message: prefer API body message, else catalog by code.
 * Axios's generic "Request failed with status code NNN" is ignored.
 */
export function getOtpUserMessage(error, fallback = "Something went wrong. Please try again.") {
  const code = preferApiErrorCode(error);

  const apiBodyMessage =
    (typeof error?.response?.data?.message === "string" &&
      error.response.data.message.trim()) ||
    (typeof error?.response?.data?.errors?.[0]?.message === "string" &&
      error.response.data.errors[0].message.trim()) ||
    "";
  if (apiBodyMessage) return apiBodyMessage;

  const rawMessage =
    typeof error?.message === "string" ? error.message.trim() : "";
  if (rawMessage && !isAxiosStatusMessage(rawMessage)) return rawMessage;

  switch (code) {
    case OTP_ERROR_CODES.OTP_INVALID:
      return "That code is incorrect. Please try again or request a new code.";
    case OTP_ERROR_CODES.OTP_EXPIRED:
      return "That code has expired. Request a new code to continue.";
    case OTP_ERROR_CODES.OTP_LIMIT:
      return "Too many attempts. Please wait about 15 minutes before trying this email again.";
    case OTP_ERROR_CODES.COGNITO_UNAVAILABLE:
      return "We're having trouble verifying your code right now. Please try again in a few minutes.";
    case OTP_ERROR_CODES.OTP_FAILED:
      return "We couldn't verify that code. Please try again or request a new one.";
    default:
      return fallback;
  }
}
